import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, fileUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LogOut, Shield, Bell, Radar as RadarIcon, Camera, ChevronRight } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";

export default function Profile() {
  const { user, condo, logout, updateUser } = useAuth();
  const nav = useNavigate();
  const [privacy, setPrivacy] = useState(user.privacy || { show_unit: false, show_phone: false });
  const [uploading, setUploading] = useState(false);

  const savePrivacy = async (patch) => {
    const next = { ...privacy, ...patch };
    setPrivacy(next);
    try { await api.put("/auth/profile", { privacy: next }); updateUser({ privacy: next }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadAvatar = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/auth/profile", { avatar_url: data.path });
      updateUser({ avatar_url: data.path });
      toast.success("Foto atualizada!");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const initials = user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-5">
      <div className="glass rounded-3xl p-6 text-center">
        <div className="relative inline-block">
          {user.avatar_url ? (
            <img src={fileUrl(user.avatar_url)} alt="" className="w-24 h-24 rounded-full object-cover mx-auto" />
          ) : (
            <div className="w-24 h-24 rounded-full brand-gradient flex items-center justify-center text-white text-3xl font-bold mx-auto glow-primary">{initials}</div>
          )}
          <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full glass-strong border border-white/10 flex items-center justify-center cursor-pointer" data-testid="avatar-upload">
            <Camera className="w-4 h-4" />
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadAvatar(e.target.files[0])} />
          </label>
        </div>
        <h1 className="font-display font-bold text-xl mt-3">{user.name}</h1>
        <p className="text-sm text-muted-foreground">{condo?.name}</p>
        <div className="flex justify-center gap-2 mt-3">
          <span className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10">{user.tower}</span>
          <span className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10">Ap {user.unit}</span>
        </div>
      </div>

      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="text-xs uppercase tracking-wider text-primary font-medium">Dados de contato</p>
        <Row label="E-mail" value={user.email} />
        <Row label="Celular" value={user.phone} />
      </div>

      <div className="glass rounded-3xl overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center gap-2"><Shield className="w-4 h-4 text-primary" /><p className="text-sm font-semibold">Privacidade (LGPD)</p></div>
        <div className="flex items-center gap-3 p-4 border-b border-white/5">
          <div className="flex-1"><p className="text-sm">Mostrar meu apartamento</p><p className="text-xs text-muted-foreground">Ao publicar na comunidade</p></div>
          <Switch data-testid="privacy-unit" checked={privacy.show_unit} onCheckedChange={(v) => savePrivacy({ show_unit: v })} />
        </div>
        <div className="flex items-center gap-3 p-4">
          <div className="flex-1"><p className="text-sm">Mostrar meu telefone</p><p className="text-xs text-muted-foreground">Para outros moradores</p></div>
          <Switch data-testid="privacy-phone" checked={privacy.show_phone} onCheckedChange={(v) => savePrivacy({ show_phone: v })} />
        </div>
      </div>

      <ThemeToggle />

      <div className="glass rounded-3xl divide-y divide-white/5 overflow-hidden">
        <NavRow icon={RadarIcon} label="Meu Radar" onClick={() => nav("/app/radar")} />
        <NavRow icon={Bell} label="Notificações" onClick={() => nav("/app/notificacoes")} />
      </div>

      <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center justify-center gap-2 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 py-3 font-medium hover:bg-red-500/15 transition-colors">
        <LogOut className="w-4 h-4" /> Sair
      </button>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>
);
const NavRow = ({ icon: Icon, label, onClick }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.04] transition-colors">
    <Icon className="w-4 h-4 text-primary" /><span className="flex-1 text-left text-sm">{label}</span><ChevronRight className="w-4 h-4 text-muted-foreground" />
  </button>
);
